import React, { useState } from 'react';
import LucideIcon from './LucideIcon';

export interface AccordionItem {
  id: string;
  title: string;
  content: string;
  examples?: string[];
}

export interface AccordionCategory {
  id: string;
  title: string;
  icon: string;
  items: AccordionItem[];
}

interface SearchableAccordionProps {
  heading: string;
  subheading: string;
  searchPlaceholder: string;
  categories: AccordionCategory[];
  defaultExpandedCategory?: string;
}

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
};

/**
 * Reference.tsx and HelpFAQ.tsx used to each hand-roll an identical
 * search-then-expand accordion -- same header, same search bar, same
 * category/item toggle logic, same empty state -- with only the content
 * arrays actually differing. One component now, so the two pages can't
 * drift out of visual sync the way Profile/GamificationHub's badge lists did.
 */
const SearchableAccordion: React.FC<SearchableAccordionProps> = ({ heading, subheading, searchPlaceholder, categories, defaultExpandedCategory }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>(defaultExpandedCategory ? [defaultExpandedCategory] : []);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };
  const toggleItem = (id: string) => {
    setExpandedItems((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const term = searchTerm.toLowerCase();
  const filtered = categories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => item.title.toLowerCase().includes(term) || item.content.toLowerCase().includes(term)),
    }))
    .filter((category) => category.items.length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 860 }}>
      <div className="ws-fade-up" style={{ ...panel, padding: 22 }}>
        <h1 className="ws-display" style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{heading}</h1>
        <p style={{ margin: '0 0 16px', fontSize: 13.5, color: 'var(--text-secondary)' }}>{subheading}</p>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }}>
            <LucideIcon name="search" size={16} />
          </span>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={searchPlaceholder}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '11px 14px 11px 38px', fontSize: 13.5,
              color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)',
              borderRadius: 10, outline: 'none',
            }}
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="ws-fade-up" style={{ ...panel, padding: '44px 22px', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: 'var(--text-tertiary)' }}><LucideIcon name="search" size={32} /></div>
          <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>No results</h3>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Try a different search term.</p>
        </div>
      ) : (
        filtered.map((category, i) => {
          const catOpen = expandedCategories.includes(category.id);
          return (
            <div key={category.id} className="ws-fade-up" style={{ ...panel, animationDelay: `${60 + i * 20}ms`, overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => toggleCategory(category.id)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: 'var(--ember-strong)' }}><LucideIcon name={category.icon} size={20} /></span>
                  <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{category.title}</span>
                  <span className="ws-mono" style={{ fontSize: 10.5, color: 'var(--text-tertiary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 999, padding: '2px 9px' }}>{category.items.length}</span>
                </span>
                <span style={{ color: 'var(--text-tertiary)', transform: catOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M5 7.5l5 5 5-5" /></svg>
                </span>
              </button>

              {catOpen && (
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  {category.items.map((item, idx) => {
                    const itemOpen = expandedItems.includes(item.id);
                    return (
                      <div key={item.id} style={{ borderBottom: idx !== category.items.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <button
                          type="button"
                          onClick={() => toggleItem(item.id)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 22px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                        >
                          <span style={{ fontSize: 13.5, fontWeight: 600, color: itemOpen ? 'var(--ember-strong)' : 'var(--text-primary)' }}>{item.title}</span>
                          <span style={{ color: 'var(--text-tertiary)', flex: '0 0 auto', marginLeft: 12, transform: itemOpen ? 'rotate(45deg)' : 'none', transition: 'transform 150ms', fontSize: 18, lineHeight: 1 }}>+</span>
                        </button>
                        {itemOpen && (
                          <div className="ws-rise" style={{ padding: '0 22px 18px' }}>
                            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.65, color: 'var(--text-secondary)' }}>{item.content}</p>
                            {item.examples && item.examples.length > 0 && (
                              <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 8, background: 'var(--surface-sunken)', border: '1px solid var(--border)' }}>
                                <p className="ws-mono" style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>Examples</p>
                                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
                                  {item.examples.map((ex) => (
                                    <li key={ex} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5 }}>
                                      <span style={{ color: 'var(--ember-strong)' }}>•</span>
                                      <code className="ws-mono" style={{ color: 'var(--ember-strong)' }}>{ex}</code>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default SearchableAccordion;
